<template>
  <div class="connect-return">
    <div class="connect-return-container">
      <div class="connect-return-content">
        <div v-if="loading" class="loading-state">
          <div class="spinner"></div>
          <h2>Completing your payout setup...</h2>
          <p>Please wait while we finalize your account setup.</p>
        </div>
        
        <div v-else-if="error" class="error-state">
          <div class="error-icon">
            <i class="fas fa-exclamation-circle"></i>
          </div>
          <h2>Setup Incomplete</h2>
          <p>{{ error }}</p>
          <button @click="redirectToProTips" class="retry-btn">
            Return to Pro Tips
          </button>
        </div>
        
        <div v-else-if="success" class="success-state">
          <div class="success-icon">
            <i class="fas fa-check-circle"></i>
          </div>
          <h2>Payout Setup Complete!</h2>
          <p v-if="pendingAmount > 0">
            Great news! €{{ pendingAmount.toFixed(2) }} in pending earnings will be transferred to your account.
          </p>
          <p v-else>
            Your payout account is now ready to receive payments from tips.
          </p>
          <button @click="redirectToProTips" class="continue-btn">
            Continue to Pro Tips
          </button>
        </div>
        
        <div v-else class="partial-success-state">
          <div class="warning-icon">
            <i class="fas fa-exclamation-triangle"></i>
          </div>
          <h2>Setup In Progress</h2>
          <p>Your account has been created, but you may need to complete additional steps with Stripe to enable payouts.</p>
          <button @click="redirectToProTips" class="continue-btn">
            Return to Pro Tips
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";

export default {
  name: "StripeConnectReturn",
  data() {
    return {
      loading: true,
      success: false,
      error: null,
      pendingAmount: 0,
      redirectTimer: null
    };
  },
  async created() {
    await this.completeConnectOnboarding();
  },
  beforeUnmount() {
    if (this.redirectTimer) {
      clearTimeout(this.redirectTimer);
    }
  },
  methods: {
    async completeConnectOnboarding() {
      try {
        const token = localStorage.getItem("access_token");
        
        if (!token) {
          this.error = "You must be logged in to complete payout setup.";
          this.loading = false;
          return;
        }

        const response = await axios.post(
          `https://api.6buoys.com/payments/connect/complete-onboarding/`,
          {},
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        );
        
        if (response.data.success) {
          this.success = response.data.payouts_enabled;
          this.pendingAmount = response.data.pending_amount || 0;
          
          if (!response.data.payouts_enabled) {
            // Onboarding complete but payouts not yet enabled
            this.success = false;
          }
          
          // Auto-redirect after 3 seconds on success
          if (this.success) {
            this.redirectTimer = setTimeout(() => {
              this.redirectToProTips();
            }, 3000);
          }
        } else {
          this.error = "Failed to complete account setup. Please try again.";
        }
        
      } catch (error) {
        console.error("Error completing Connect onboarding:", error);
        
        if (error.response?.data?.error) {
          this.error = error.response.data.error;
        } else if (error.response?.status === 401) {
          this.error = "Your session has expired. Please log in again.";
        } else {
          this.error = "An unexpected error occurred. Please contact support if the problem persists.";
        }
      } finally {
        this.loading = false;
      }
    },
    redirectToProTips() {
      this.$router.push('/pro-tip-requests');
    }
  }
};
</script>

<style scoped>
.connect-return {
  min-height: 100vh;
  background-color: #15202b;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
}

.connect-return-container {
  width: 100%;
  max-width: 500px;
}

.connect-return-content {
  background-color: #192734;
  border-radius: 12px;
  padding: 40px;
  text-align: center;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
}

.loading-state,
.success-state,
.error-state,
.partial-success-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

.spinner {
  width: 50px;
  height: 50px;
  border: 4px solid #38444d;
  border-top: 4px solid #1da1f2;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.success-icon {
  font-size: 60px;
  color: #17bf63;
}

.error-icon {
  font-size: 60px;
  color: #e0245e;
}

.warning-icon {
  font-size: 60px;
  color: #ffad1f;
}

h2 {
  margin: 0;
  color: #ffffff;
  font-size: 24px;
  font-weight: bold;
}

p {
  margin: 0;
  color: #d9d9d9;
  line-height: 1.5;
  font-size: 16px;
}

.continue-btn,
.retry-btn {
  padding: 12px 24px;
  background-color: #1da1f2;
  color: #ffffff;
  border: none;
  border-radius: 24px;
  cursor: pointer;
  font-weight: bold;
  font-size: 16px;
  transition: background-color 0.3s ease;
  margin-top: 8px;
}

.continue-btn:hover,
.retry-btn:hover {
  background-color: #0c7abf;
}

.retry-btn {
  background-color: #8899a6;
}

.retry-btn:hover {
  background-color: #657786;
}

/* Mobile responsive */
@media screen and (max-width: 600px) {
  .connect-return-content {
    padding: 30px 20px;
  }
  
  h2 {
    font-size: 20px;
  }
  
  p {
    font-size: 14px;
  }
  
  .success-icon,
  .error-icon,
  .warning-icon {
    font-size: 50px;
  }
}
</style> 